"use client";

import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  onClose: () => void;
}

const CustomToast: React.FC<ToastProps> = ({ 
  message, 
  type = 'info', 
  duration = 3000, 
  onClose 
}) => {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300); // Wait for animation to finish
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const icons = {
    success: 'mdi:check-circle',
    error: 'mdi:close-circle',
    warning: 'mdi:alert-circle',
    info: 'mdi:information',
  };

  const colors = {
    success: 'bg-green-500',
    error: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500',
  };

  const bgColors = {
    success: 'bg-green-50 border-green-200',
    error: 'bg-red-50 border-red-200',
    warning: 'bg-yellow-50 border-yellow-200',
    info: 'bg-blue-50 border-blue-200',
  };

  const textColors = {
    success: 'text-green-800',
    error: 'text-red-800',
    warning: 'text-yellow-800',
    info: 'text-blue-800',
  };

  return (
    <div
      className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg border shadow-lg transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
      } ${bgColors[type]}`}
    >
      <div className={`w-8 h-8 rounded-full ${colors[type]} flex items-center justify-center`}>
        <Icon icon={icons[type]} className="text-white text-lg" />
      </div>
      <p className={`font-family-open-sans text-sm ${textColors[type]}`}>{message}</p>
      <button
        onClick={() => {
          setIsVisible(false);
          setTimeout(onClose, 300);
        }}
        className={`ml-2 ${textColors[type]} hover:opacity-70 transition-opacity`}
      >
        <Icon icon="mdi:close" className="text-lg" />
      </button>
    </div>
  );
};

// Toast Container to manage multiple toasts
interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
}

let toastId = 0;
let addToastCallback: ((toast: Omit<Toast, 'id'>) => void) | null = null;

export const toast = {
  success: (message: string) => addToastCallback?.({ message, type: 'success' }),
  error: (message: string) => addToastCallback?.({ message, type: 'error' }),
  warning: (message: string) => addToastCallback?.({ message, type: 'warning' }),
  info: (message: string) => addToastCallback?.({ message, type: 'info' }),
};

export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    addToastCallback = (newToast) => {
      setToasts((prev) => [...prev, { ...newToast, id: ++toastId }]);
    };

    return () => {
      addToastCallback = null;
    };
  }, []);

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2">
      {toasts.map((t) => (
        <CustomToast
          key={t.id}
          message={t.message}
          type={t.type}
          onClose={() => removeToast(t.id)}
        />
      ))}
    </div>
  );
};

export default CustomToast;